/**
 * BulkApplyBar Component
 *
 * Sticky action bar that appears when classification rules are selected.
 * Allows applying multiple rules to transactions with scope selection.
 *
 * Features:
 * - Shows selected count
 * - Scope dropdown (pending only, exclude manual, all)
 * - Apply Rules button with loading state
 * - Clear selection button
 * - Sticky positioning at bottom of viewport
 * - Slide-in animation
 */

import { useCallback, useState } from 'react';
import clsx from 'clsx';
import { CheckSquare, PlayCircle, X, ChevronDown } from 'lucide-react';

export type ApplyScope = 'pending_only' | 'exclude_manual' | 'all';

export interface BulkApplyBarProps {
  /** Number of currently selected rules */
  selectedCount: number;
  /** Callback when "Apply Rules" is clicked */
  onApply: (scope: ApplyScope) => void;
  /** Callback to clear all selections */
  onClearSelection: () => void;
  /** Whether a bulk operation is currently in progress */
  isLoading?: boolean;
}

const SCOPE_OPTIONS: { value: ApplyScope; label: string; description: string }[] = [
  {
    value: 'pending_only',
    label: 'Only pending transactions',
    description: 'Apply to transactions that haven\'t been classified yet',
  },
  {
    value: 'exclude_manual',
    label: 'Exclude manually classified',
    description: 'Apply to pending and rule-matched, skip manual',
  },
  {
    value: 'all',
    label: 'All transactions',
    description: 'Apply to all matching transactions, overwriting existing',
  },
];

/**
 * BulkApplyBar renders a sticky action bar at the bottom of the screen
 * for bulk applying classification rules.
 *
 * The bar is only visible when at least one rule is selected.
 *
 * @example
 * ```tsx
 * {selectedRuleIds.size > 0 && (
 *   <BulkApplyBar
 *     selectedCount={selectedRuleIds.size}
 *     onApply={handleBulkApply}
 *     onClearSelection={() => setSelectedRuleIds(new Set())}
 *     isLoading={isApplying}
 *   />
 * )}
 * ```
 */
export function BulkApplyBar({
  selectedCount,
  onApply,
  onClearSelection,
  isLoading = false,
}: BulkApplyBarProps) {
  const [scope, setScope] = useState<ApplyScope>('pending_only');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleApply = useCallback(() => {
    if (!isLoading && selectedCount > 0) {
      onApply(scope);
    }
  }, [onApply, isLoading, selectedCount, scope]);

  const handleClear = useCallback(() => {
    if (!isLoading) {
      onClearSelection();
    }
  }, [onClearSelection, isLoading]);

  const handleScopeChange = useCallback((newScope: ApplyScope) => {
    setScope(newScope);
    setIsDropdownOpen(false);
  }, []);

  // Don't render if nothing is selected
  if (selectedCount === 0) {
    return null;
  }

  const currentScopeOption = SCOPE_OPTIONS.find((opt) => opt.value === scope);

  return (
    <div
      className={clsx(
        'fixed bottom-0 left-0 right-0 z-50',
        'bg-white border-t border-gray-200 shadow-lg',
        'animate-in slide-in-from-bottom-4 duration-200'
      )}
      role="toolbar"
      aria-label="Bulk apply rules"
      data-testid="bulk-apply-bar"
    >
      <div className="max-w-4xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Selection info */}
          <div className="flex items-center gap-2">
            <CheckSquare
              className="w-5 h-5 text-blue-600"
              aria-hidden="true"
            />
            <span className="text-sm font-medium text-gray-900">
              {selectedCount} rule{selectedCount !== 1 ? 's' : ''} selected
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {/* Scope dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                disabled={isLoading}
                className={clsx(
                  'flex items-center gap-2 px-3 py-1.5 text-sm',
                  'border rounded-md',
                  'transition-colors duration-150',
                  isLoading
                    ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500'
                )}
                data-testid="scope-dropdown-button"
                aria-haspopup="listbox"
                aria-expanded={isDropdownOpen}
              >
                <span className="max-w-[200px] truncate">
                  {currentScopeOption?.label}
                </span>
                <ChevronDown
                  className={clsx(
                    'w-4 h-4 transition-transform',
                    isDropdownOpen && 'rotate-180'
                  )}
                  aria-hidden="true"
                />
              </button>

              {isDropdownOpen && (
                <>
                  {/* Backdrop to close dropdown */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsDropdownOpen(false)}
                  />
                  {/* Dropdown menu */}
                  <div
                    className="absolute bottom-full mb-2 right-0 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-20"
                    role="listbox"
                    data-testid="scope-dropdown-menu"
                  >
                    {SCOPE_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleScopeChange(option.value)}
                        className={clsx(
                          'w-full px-4 py-3 text-left',
                          'hover:bg-gray-50 transition-colors',
                          'first:rounded-t-lg last:rounded-b-lg',
                          option.value === scope && 'bg-blue-50'
                        )}
                        role="option"
                        aria-selected={option.value === scope}
                        data-testid={`scope-option-${option.value}`}
                      >
                        <div className="text-sm font-medium text-gray-900">
                          {option.label}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {option.description}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Apply button */}
            <button
              type="button"
              onClick={handleApply}
              disabled={isLoading || selectedCount === 0}
              className={clsx(
                'flex items-center gap-2 px-4 py-1.5 text-sm font-medium',
                'rounded-md',
                'transition-colors duration-150',
                isLoading || selectedCount === 0
                  ? 'bg-blue-300 text-white cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
              )}
              data-testid="bulk-apply-button"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Applying...
                </>
              ) : (
                <>
                  <PlayCircle className="w-4 h-4" aria-hidden="true" />
                  Apply Rules
                </>
              )}
            </button>

            {/* Clear selection button */}
            <button
              type="button"
              onClick={handleClear}
              disabled={isLoading}
              className={clsx(
                'flex items-center gap-1 p-1.5',
                'rounded-md',
                'transition-colors duration-150',
                isLoading
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
                'focus:outline-none focus:ring-2 focus:ring-blue-500'
              )}
              aria-label="Clear selection"
              data-testid="clear-selection-button"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BulkApplyBar;
